import { motion } from 'framer-motion';
import { useLanguage } from '../../i18n/LanguageContext';

/**
 * Follow-ups section — the killer USP.
 * Split layout: narrative on the left, chat-bubble mockup on the right
 * showing a proactive next-day message from the companion.
 */
export default function Followups() {
  const { t } = useLanguage();
  if (!t.fu_chat) return null;

  return (
    <section
      id="follow-ups"
      className="bg-ev-ink py-24 md:py-32 px-6 md:px-10 relative overflow-hidden"
      data-testid="followups-section">
      {/* Subtle radial accent */}
      <div
        aria-hidden="true"
        className="absolute -top-32 -left-32 w-[28rem] h-[28rem] rounded-full opacity-[0.08] blur-3xl"
        style={{ background: 'radial-gradient(circle, #dcf57b, transparent 60%)' }}
      />
      <div className="max-w-[1300px] mx-auto relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* LEFT — narrative */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}>
            <p className="font-body text-[10px] uppercase tracking-[0.4em] text-ev-lime mb-6">{t.fu_label}</p>
            <h2
              className="font-display font-light text-white lowercase mb-6"
              style={{ fontSize: 'clamp(2.2rem, 5.2vw, 4.25rem)', letterSpacing: '-0.03em' }}>
              {t.fu_h}
            </h2>
            <p className="font-body text-base md:text-lg text-white/70 leading-relaxed mb-10 max-w-[520px]">
              {t.fu_sub}
            </p>

            {t.fu_meta && (
              <ul className="space-y-3 max-w-[460px]" data-testid="fu-meta">
                {t.fu_meta.map((m, i) => (
                  <li
                    key={i}
                    className="flex items-baseline gap-4 border-t border-white/10 pt-3"
                    data-testid={`fu-meta-${i}`}>
                    <span className="font-body text-[10px] uppercase tracking-[0.25em] text-ev-lime/80 shrink-0 w-44">
                      {m.k}
                    </span>
                    <span className="font-body text-sm md:text-base text-white/85 leading-snug">{m.v}</span>
                  </li>
                ))}
              </ul>
            )}
          </motion.div>

          {/* RIGHT — chat mockup */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="relative">
            <div
              className="rounded-[1.75rem] bg-white/[0.04] border border-white/10 p-5 md:p-7 max-w-[460px] mx-auto"
              data-testid="fu-chat-mockup">
              {/* Mock chat header */}
              <div className="flex items-center gap-3 pb-4 border-b border-white/10 mb-5">
                <div className="w-9 h-9 rounded-full bg-ev-lime flex items-center justify-center">
                  <span className="font-display text-ev-ink text-sm lowercase">e</span>
                </div>
                <div className="leading-tight">
                  <p className="font-body text-sm text-white lowercase">{t.fu_chat_name}</p>
                  <p className="font-body text-[11px] text-white/40 lowercase">{t.fu_chat_status}</p>
                </div>
              </div>

              {/* Chat bubbles */}
              <div className="space-y-3">
                {t.fu_chat.map((msg, i) => {
                  const isMe = msg.from === 'you';
                  const isProactive = msg.proactive;
                  return (
                    <div key={i} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[82%] flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                        {isProactive && (
                          <div className="mb-1.5 px-2.5 py-0.5 rounded-full bg-ev-lime/20 border border-ev-lime/40">
                            <span className="font-body text-[9px] uppercase tracking-[0.2em] text-ev-lime">
                              {t.fu_chat_proactive_tag}
                            </span>
                          </div>
                        )}
                        <div
                          className={`px-4 py-2.5 rounded-[1.25rem] ${
                            isMe
                              ? 'bg-white/10 text-white rounded-br-md'
                              : isProactive
                              ? 'bg-ev-lime text-ev-ink rounded-bl-md'
                              : 'bg-white text-ev-ink rounded-bl-md'
                          }`}
                          data-testid={`fu-bubble-${i}`}>
                          <p className="font-body text-sm md:text-[15px] leading-snug">{msg.text}</p>
                        </div>
                        <span
                          className={`font-body text-[10px] mt-1 lowercase ${
                            isMe ? 'text-white/40' : 'text-white/40'
                          }`}>
                          {msg.time}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Caption beneath chat */}
            {t.fu_caption && (
              <p className="mt-6 text-center font-body text-xs md:text-sm text-ev-lime/80 lowercase tracking-wide">
                {t.fu_caption}
              </p>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
